import { Card, Spacing, Text } from '@vkontakte/vkui';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getB2bBotToken } from 'reactApp/api/axios.corsapi';
import { multiSessionRestrictionConfig } from 'reactApp/appHelpers/featuresHelpers/features/multiSessionRestriction';
import { useAsyncValue } from 'reactApp/hooks/useAsyncValue';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import { Dialog } from 'reactApp/ui/Dialog/Dialog';
import { B2bBotQr } from 'reactApp/ui/MultiCookieWarningModal/MultiCookieQr/MultiCookieQr';
import { ScreenNew } from 'reactApp/ui/ScreenNew/ScreenNew';
import { sendDwh } from 'reactApp/utils/ga';
import opener from 'reactApp/utils/opener';
import { computersGenitive } from 'reactApp/utils/pluralHelpers';

import styles from './MultiCookieWarningModal.css';

export interface MultiCookieWarningModalProps {
    onClose?: () => void;
    closable?: boolean;
    mode: 'fullscreen' | 'modal';
}

const sendRadar = (action: 'show' | 'click' | 'close' | 'click-other') => {
    sendDwh({
        eventCategory: 'modal',
        action,
        dwhData: { name: 'multi_cookie_landing_warning' },
    });
};

const messages = {
    fullscreenTitle: (
        <>
            Подключение совместной работы в Почте
            <br />
            доступно только в&nbsp;чат-боте
        </>
    ),
    fullscreenDescription: (email: string) => `В\u00A0чат-боте сможете выбрать нужное количество для\u00A0участников аккаунта ${email}`,
    modalTitle: 'В этом тарифе нет совместной работы в Почте',
    modalDescription: (limit: number) =>
        `Подключить её\u00A0можно только в\u00A0чат-боте. Перейдите в\u00A0него по\u00A0QR выше, если\u00A0с\u00A0телефона удобнее и\u00A0выберите подходящее число сотрудников. Сможете работать с\u00A0одного аккаунта больше, чем\u00A0с\u00A0${limit}\u00A0${computersGenitive(
            limit
        )}`,
    fullscreenPrimaryButton: 'Подключить с\u00A0компьютера',
    fullscreenSecondaryButton: 'Другие тарифы',
    modalPrimaryButton: 'Купить совместную работу',
    modalSecondaryButton: 'Продолжить без совместной работы ',
};

const modalId = 'multi-cookie-warning-modal';

export const MultiCookieWarningModal = ({ onClose, mode, closable = true }: MultiCookieWarningModalProps) => {
    const { data, isLoading } = useAsyncValue(() => getB2bBotToken('need_pay'), { token: '' }, []);
    const { token } = data;

    const email = useSelector(UserSelectors.getEmail);

    const url = multiSessionRestrictionConfig.max_bot_url + (token ? `?start=${token}` : '');
    const isModal = mode === 'modal';

    useEffect(() => {
        sendRadar('show');
    }, []);

    const handleClose = useCallback(() => {
        onClose?.();
        sendRadar('close');
    }, []);

    const onPrimaryClick = useCallback(() => {
        sendRadar('click');
        opener(url);
    }, [url]);

    const onSecondaryClick = useCallback(() => {
        sendRadar('click-other');
        onClose?.();
    }, []);

    const content = useMemo(
        () => (
            <div className={styles.root}>
                <div className={styles.qrBlock}>
                    <B2bBotQr url={url} isLoading={isLoading} />
                </div>
                <Spacing size={40} />
                {isModal && (
                    <>
                        <Text className={styles.title}>{messages.modalTitle}</Text>
                        <Spacing size={12} />
                    </>
                )}
                {!isModal && <Spacing size={35} />}
                <Text className={styles.text}>
                    {isModal ? messages.modalDescription(multiSessionRestrictionConfig.limit) : messages.fullscreenDescription(email ?? '')}
                </Text>
                <Spacing size={24} />
                <div className={styles.buttons}>
                    <Button theme="octavius" primary sizeMode={ButtonSizeMode.small} onClick={onPrimaryClick}>
                        {isModal ? messages.modalPrimaryButton : messages.fullscreenPrimaryButton}
                    </Button>
                    {!multiSessionRestrictionConfig.modal.disable_tariffs_button && (
                        <Button theme="octavius" sizeMode={ButtonSizeMode.small} onClick={onSecondaryClick}>
                            {isModal ? messages.modalSecondaryButton : messages.fullscreenSecondaryButton}
                        </Button>
                    )}
                </div>
            </div>
        ),
        [isLoading, url, email, onPrimaryClick, onSecondaryClick]
    );

    if (mode === 'fullscreen') {
        return (
            <ScreenNew
                closable={closable}
                onClose={handleClose}
                contentClassName={styles.fullscreenContent}
                isDarkTheme
                withHeader
                className={styles.fullscreenWrapper}
            >
                <>
                    <Text className={styles.fullscreenTitle}>{messages.fullscreenTitle}</Text>
                    <Card className={styles.fullscreenCard}>{content}</Card>
                </>
            </ScreenNew>
        );
    }

    return (
        <Dialog open id={modalId} onCancel={handleClose} size={'tiny'} closeOnDimmerClick={false} closable={closable}>
            {content}
        </Dialog>
    );
};
